/** @jsxImportSource preact */
import { useState } from 'preact/hooks'
import { config } from '../config'
import { useAuth } from '../providers/auth'
import { forEachConcurrently, parseBookingIds } from './BulkRefundClose'
import { previewTicketedOutside, type TeamCreateReview } from './bulk-ticketed-outside'

interface Row {
  bookingId: string
  action: 'pending' | 'running' | 'would-close' | 'closed' | 'skipped' | 'error'
  reason: string
  workflowId?: string
}

type Phase = 'idle' | 'previewing' | 'previewed' | 'closing' | 'done'

const CONCURRENCY = 5

const MAX_BOOKINGS = 50

const BADGE_CLASSES: Record<Row['action'], string> = {
  pending: 'bg-gray-100 text-gray-700',
  running: 'bg-gray-100 text-gray-700',
  'would-close': 'bg-blue-100 text-blue-800',
  closed: 'bg-green-100 text-green-800',
  skipped: 'bg-amber-100 text-amber-800',
  error: 'bg-red-100 text-red-800',
}

async function fetchJson(url: string, init: RequestInit = {}): Promise<unknown> {
  const resp = await fetch(url, { ...init, credentials: 'include' })
  const body = await resp.json().catch(() => ({}))
  if (!resp.ok) {
    throw new Error(body.error ?? `Request failed (${resp.status})`)
  }

  return body
}

export function BulkTicketedOutside() {
  const { user } = useAuth()
  const [input, setInput] = useState('')
  const [note, setNote] = useState('')
  const [checkedInSabre, setCheckedInSabre] = useState(false)
  const [rows, setRows] = useState<Row[]>([])
  const [phase, setPhase] = useState<Phase>('idle')
  const [error, setError] = useState('')
  const [progress, setProgress] = useState({ done: 0, total: 0 })

  const { ids, rejected } = parseBookingIds(input)
  const closable = rows.filter(r => r.action === 'would-close')
  const running = phase === 'previewing' || phase === 'closing'
  const overCap = ids.length > MAX_BOOKINGS

  const updateRow = (bookingId: string, row: Partial<Row>) =>
    setRows(current => current.map(r => (r.bookingId === bookingId ? { ...r, ...row } : r)))

  // One request per booking, so a slow one holds up only its own row.
  const runRows = async (
    bookingIds: string[],
    runningLabel: string,
    request: (bookingId: string) => Promise<Partial<Row>>,
  ) => {
    setProgress({ done: 0, total: bookingIds.length })

    await forEachConcurrently(bookingIds, CONCURRENCY, async bookingId => {
      updateRow(bookingId, { action: 'running', reason: runningLabel })

      try {
        updateRow(bookingId, await request(bookingId))
      } catch (err) {
        updateRow(bookingId, { action: 'error', reason: err instanceof Error ? err.message : 'Unknown error' })
      } finally {
        setProgress(current => ({ ...current, done: current.done + 1 }))
      }
    })
  }

  const preview = async () => {
    if (ids.length === 0) {
      setError('Enter at least one booking id')
      return
    }

    setError('')
    setCheckedInSabre(false)
    setPhase('previewing')
    setRows(ids.map(bookingId => ({ bookingId, action: 'pending', reason: '' })))

    await runRows(ids, 'Reading…', async bookingId => {
      const reviews = await fetchJson(`${config.apiBaseUrl}/bookings/${bookingId}/fulfilments/team-create`)

      return previewTicketedOutside(reviews as TeamCreateReview[])
    })

    setPhase('previewed')
  }

  const close = async () => {
    if (!note.trim()) {
      setError('Note is required')
      return
    }

    setError('')
    setPhase('closing')

    const username = user && (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email)
    const reason = username ? `${note.trim()} - submitted by ${username}` : note.trim()

    const workflowIds = new Map(closable.map(row => [row.bookingId, row.workflowId]))

    await runRows([...workflowIds.keys()], 'Closing…', async bookingId => {
      await fetchJson(`${config.apiBaseUrl}/bookings/team-create/finalise`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workflowId: workflowIds.get(bookingId),
          passed: true,
          ticketed_outside_ops_app: true,
          reason,
        }),
      })

      return { action: 'closed', reason: 'Closed as ticketed outside the Ops App' }
    })

    setPhase('done')
  }

  const reset = () => {
    setInput('')
    setNote('')
    setCheckedInSabre(false)
    setRows([])
    setPhase('idle')
    setError('')
    setProgress({ done: 0, total: 0 })
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <h1 className="text-2xl font-semibold mb-1">Close Create reviews ticketed outside the Ops App</h1>
      <p className="text-gray-600 mb-6">
        For bookings already ticketed outside the Ops App, for example split into two PNRs in Sabre or queued to the
        supplier by another team. Closing records the booking as fulfilled and adds a note to the deal. Nothing is sent
        for ticketing and nothing is cancelled.
      </p>

      <div className="space-y-4">
        <div>
          <label
            htmlFor="bulkTicketedOutsideIds"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Booking IDs <span className="text-red-500">*</span>
          </label>
          <textarea
            id="bulkTicketedOutsideIds"
            rows={8}
            value={input}
            onChange={e => setInput((e.target as HTMLTextAreaElement).value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
            placeholder={'One booking id per line, or paste a CSV column — e.g.\nPLT3001935\nPLT2980669'}
          />
          <p className="mt-1 text-xs text-gray-500">
            {ids.length} booking id(s) recognised. Preview shows which ones can be closed and why the others cannot.
          </p>
          {overCap && (
            <p className="mt-1 text-xs text-red-600">
              {ids.length} is more than the {MAX_BOOKINGS} this runs in one go. Split the list.
            </p>
          )}
          {rejected.length > 0 && (
            <p className="mt-1 text-xs text-red-600">
              Not booking ids, and ignored: <span className="font-mono">{rejected.join(', ')}</span>. Use the PLT
              booking id, not the PNR.
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="bulkTicketedOutsideNote"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Note <span className="text-red-500">*</span>
          </label>
          <textarea
            id="bulkTicketedOutsideNote"
            rows={3}
            value={note}
            onChange={e => setNote((e.target as HTMLTextAreaElement).value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Who ticketed them and where, e.g. Split into two PNRs (JetBlue 7-seat limit) and queued in Sabre by the Create team"
          />
          <p className="mt-1 text-xs text-gray-500">Added to every closed booking's deal.</p>
        </div>

        {phase === 'previewed' && closable.length > 0 && (
          <label className="flex items-start gap-2 text-sm text-amber-800 cursor-pointer">
            <input
              type="checkbox"
              checked={checkedInSabre}
              onChange={e => setCheckedInSabre((e.target as HTMLInputElement).checked)}
              className="mt-0.5 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <span>I have checked in Sabre that every booking marked would-close is already ticketed.</span>
          </label>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={preview}
            disabled={running || ids.length === 0 || overCap}
            className="px-4 py-2 bg-gray-100 text-gray-800 font-medium rounded-md shadow-sm hover:bg-gray-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {phase === 'previewing' ? `Reading ${progress.done} of ${progress.total}…` : 'Preview'}
          </button>
          <button
            type="button"
            onClick={close}
            disabled={running || phase !== 'previewed' || closable.length === 0 || !checkedInSabre || !note.trim()}
            title={phase !== 'previewed' ? 'Preview first' : undefined}
            className="px-4 py-2 bg-gray-700 text-white font-medium rounded-md shadow-sm hover:bg-gray-800 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {phase === 'closing'
              ? `Closing ${progress.done} of ${progress.total}…`
              : `Close ${closable.length} booking(s)`}
          </button>
          <button
            type="button"
            onClick={reset}
            disabled={running}
            className="px-4 py-2 bg-white text-gray-700 font-medium border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            Reset
          </button>
        </div>

        {rows.length > 0 && (
          <div className="mt-2 border border-gray-200 rounded-md overflow-hidden overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">Booking</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">Result</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map(row => (
                  <tr key={row.bookingId}>
                    <td className="px-3 py-2 font-mono whitespace-nowrap">{row.bookingId}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={'px-2 py-0.5 rounded-full text-xs font-medium ' + BADGE_CLASSES[row.action]}>
                        {row.action}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{row.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {phase === 'done' && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-md">
            <p className="text-green-700">
              Closed {rows.filter(r => r.action === 'closed').length} booking(s). Preview again to confirm none are
              still waiting.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
